// This Source Code Form is subject to the terms of the Mozilla Public
// License, v. 2.0. If a copy of the MPL was not distributed with this
// file, You can obtain one at https://mozilla.org/MPL/2.0/.
// Telemetry panel component with managed focus
// Spec §2: callsign, registration, type, operator, altitude, speed, heading,
// vertical rate, squawk, data age.
// Spec §6: managed focus (moves in on select, returns on close, Esc to close) — not a focus trap.

import React, { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../icons/Icon';import type { TFunction } from 'i18next';
import type { DisplayAircraft } from '../types';
import type { NoiseContours } from '@vliegvuil/core';
import { NoiseBadge } from './NoiseOverlay';
import { formatAltitude, formatSpeed, type UnitSettings, DEFAULT_UNITS } from '../units';

export interface TelemetryPanelProps {
  aircraft: DisplayAircraft;
  onClose: () => void;
  /** Noise contours for the membership badge (session 10) */
  noiseContours?: NoiseContours | null;
  /** Units for altitude/speed display (session 11) */
  units?: UnitSettings;
}

/** Format a nullable value, falling back to an em dash */
function formatValue(value: string | null): string {
  return value ?? '—';
}


/** Format heading in degrees */
function formatHeading(track: number | null): string {
  return track != null ? `${Math.round(track)}°` : '—';
}

/** Format vertical rate with direction indicator */
function formatVerticalRate(rate: number | null, unit: string): string {
  if (rate == null) return '—';
  const arrow = rate > 0 ? '↑' : rate < 0 ? '↓' : '';
  return `${arrow}${Math.abs(Math.round(rate)).toLocaleString()} ${unit}`;
}

/** Format data age from a Unix-seconds timestamp */
function formatDataAge(timestamp: number, t: TFunction): string {
  const ageSeconds = Math.max(0, Math.floor(Date.now() / 1000 - timestamp));
  if (ageSeconds < 60) {
    return t('telemetry.dataAgeSeconds', '{{count}}s', { count: ageSeconds });
  }
  const minutes = Math.floor(ageSeconds / 60);
  const seconds = ageSeconds % 60;
  return t('telemetry.dataAgeMinutes', '{{count}}m {{seconds}}s', { count: minutes, seconds });
}

/**
 * Telemetry panel — friendly info card for a selected aircraft.
 * Managed focus: on mount focus moves to the panel; on close (button or Esc)
 * focus returns to the previously focused element.
 */
export function TelemetryPanel({ aircraft, onClose, noiseContours, units = DEFAULT_UNITS }: TelemetryPanelProps): React.ReactElement {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<Element | null>(null);

  // Capture the previously focused element so we can restore it on close
  useEffect((): void => {
    previousFocusRef.current = document.activeElement;
    panelRef.current?.focus();
  }, []);

  // Restore focus on unmount (covers all close paths)
  useEffect((): (() => void) => {
    return (): void => {
      const previous = previousFocusRef.current;
      if (previous instanceof HTMLElement) {
        previous.focus();
      }
    };
  }, []);

  // Esc to close
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>): void => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <aside
      ref={panelRef}
      className="telemetry-panel"
      role="region"
      aria-label={t('telemetry.title', 'Vliegtuigdetails')}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      aria-live="polite"
    >
      <header className="telemetry-header">
        <h2 className="telemetry-title">
          {aircraft.callsign ?? aircraft.icao24}
        </h2>
        <button
          type="button"
          className="telemetry-close"
          onClick={onClose}
          aria-label={t('telemetry.close', 'Sluiten')}
        >
          <Icon name="x" size={18} />
        </button>
      </header>

      <dl className="telemetry-fields">
        <div className="telemetry-row">
          <dt className="telemetry-label">{t('telemetry.callsign')}</dt>
          <dd className="telemetry-value">{formatValue(aircraft.callsign)}</dd>
        </div>
        <div className="telemetry-row">
          <dt className="telemetry-label">{t('telemetry.registration')}</dt>
          <dd className="telemetry-value">{formatValue(aircraft.registration)}</dd>
        </div>
        <div className="telemetry-row">
          <dt className="telemetry-label">{t('telemetry.type')}</dt>
          <dd className="telemetry-value">{formatValue(aircraft.type)}</dd>
        </div>
        <div className="telemetry-row">
          <dt className="telemetry-label">{t('telemetry.operator')}</dt>
          <dd className="telemetry-value">{formatValue(aircraft.operator)}</dd>
        </div>
        <div className="telemetry-row">
          <dt className="telemetry-label">{t('telemetry.altitude')}</dt>
          <dd className="telemetry-value">{formatAltitude(aircraft.alt, units.altitude)}</dd>
        </div>
        <div className="telemetry-row">
          <dt className="telemetry-label">{t('telemetry.speed')}</dt>
          <dd className="telemetry-value">{formatSpeed(aircraft.speed, units.speed)}</dd>
        </div>
        <div className="telemetry-row">
          <dt className="telemetry-label">{t('telemetry.heading')}</dt>
          <dd className="telemetry-value">{formatHeading(aircraft.track)}</dd>
        </div>
        <div className="telemetry-row">
          <dt className="telemetry-label">{t('telemetry.verticalRate')}</dt>
          <dd className="telemetry-value">{formatVerticalRate(aircraft.verticalRate, t('telemetry.verticalRateUnit', 'ft/min'))}</dd>
        </div>
        <div className="telemetry-row">
          <dt className="telemetry-label">{t('telemetry.squawk')}</dt>
          <dd className="telemetry-value">{formatValue(aircraft.squawk)}</dd>
        </div>
        <div className="telemetry-row">
          <dt className="telemetry-label">{t('telemetry.dataAge')}</dt>
          <dd className="telemetry-value">{formatDataAge(aircraft.timestamp, t)}</dd>
        </div>
      </dl>

      {noiseContours && (
        <NoiseBadge lat={aircraft.lat} lon={aircraft.lon} contours={noiseContours} />
      )}
      {aircraft.isStale && (
        <p className="telemetry-badge" role="status">
          {t('map.staleData')}
        </p>
      )}
    </aside>
  );
}

export default TelemetryPanel;
